<template>
  <div>
    <RestageDropdown
      v-if="shouldDisplayRestage(activeSlideIndex) && !isImageLoading(activeSlideIndex)"
      data-test="restage-dropdown"
      :class="$style.restage"
      :currentImage="originalImageAtIndex(activeSlideIndex)"
      :currentImageAnnotation="imageAnnotationAtIndex(activeSlideIndex)"
    />

    <HgSlider
      :sliderComponents="effectiveImages"
      :nextTitle="nextTitle"
      :prevTitle="prevTitle"
      :activeSlideIndex="activeSlideIndex"
      type="loop"
      :isArrowKeysEnabled="isArrowKeysEnabled"
      :visibleSlides="1"
      :class="$style.slider"
      @slideChange="emit('slideChange', $event)"
    >
      <template #default="sliderComponent">
        <GalleryContactCta
          v-if="sliderComponent.isContactSlide"
          :variant="contactCtaVariant"
          @emailClick="emit('contactEmailClick')"
        />

        <div v-else-if="shouldShowNewConstructionBadge" :class="$style.imageContainer">
          <HgNewConstructionBadge text="short" :class="$style.HgNewConstructionBadge" />

          <div
            :class="[
              $style.galleryWrapper,
              isPlatformHg
                ? $style.newConstructionProjectImageHg
                : $style.newConstructionProjectImageIs24,
            ]"
          >
            <div
              v-if="shouldDisplayExclusiveBadge(sliderComponent.index)"
              :class="$style.badgeWrapper"
            >
              <ExclusiveImageBadge
                :showTooltip="sliderComponent.index === exclusiveStartIndex"
                :class="$style.badge"
              />
            </div>
            <div
              v-if="overlayStartIndex >= 0 && sliderComponent.index === overlayStartIndex"
              :class="$style.badgeWrapper"
            >
              <BlurredImageBadge :class="$style.badge" />
            </div>

            <div
              v-if="isImageLoading(sliderComponent.index)"
              :class="$style.loadingOverlay"
              :data-test="'loading-overlay'"
            >
              <HgSpinner :label="t('imageGallery.restage.loadingMessage')" />
            </div>

            <RestageErrorNotification
              v-if="isRestageError(sliderComponent.index)"
              :currentImage="images[sliderComponent.index]"
              :data-test="'restage-error-notification'"
            />

            <img
              :loading="sliderComponent.index === 0 ? 'eager' : 'lazy'"
              :fetchpriority="sliderComponent.index === 0 ? 'high' : 'low'"
              :src="sliderComponent.src"
              :srcset="sliderComponent.srcset"
              :sizes="sliderComponent.sizes"
              :alt="sliderComponent.alt"
              :class="[
                $style.image,
                isClickListenerDefined && !shouldDisplayOverlay(sliderComponent.index)
                  ? $style.clickEnabled
                  : '',
                $style.newConstructionProjectImage,
              ]"
              @click="handleImageClick()"
            />

            <TenantPlusGalleryModal
              v-if="shouldDisplayOverlay(sliderComponent.index)"
              @moreImagesClick="emit('moreImagesClick')"
            />
          </div>
        </div>

        <div v-else :class="$style.galleryWrapper">
          <div
            v-if="shouldDisplayExclusiveBadge(sliderComponent.index)"
            :class="$style.badgeWrapper"
          >
            <ExclusiveImageBadge
              :showTooltip="sliderComponent.index === exclusiveStartIndex"
              :class="$style.badge"
            />
          </div>
          <div
            v-if="overlayStartIndex >= 0 && sliderComponent.index === overlayStartIndex"
            :class="$style.badgeWrapper"
          >
            <BlurredImageBadge :class="$style.badge" />
          </div>

          <div
            v-if="isImageLoading(sliderComponent.index)"
            :class="$style.loadingOverlay"
            :data-test="'loading-overlay'"
          >
            <HgSpinner :label="t('imageGallery.restage.loadingMessage')" />
          </div>

          <RestageErrorNotification
            v-if="isRestageError(sliderComponent.index)"
            :currentImage="originalImageAtIndex(sliderComponent.index)"
            :data-test="'restage-error-notification'"
          />

          <img
            :loading="sliderComponent.index === 0 ? 'eager' : 'lazy'"
            :src="sliderComponent.src"
            :srcset="sliderComponent.srcset"
            :sizes="sliderComponent.sizes"
            :alt="sliderComponent.alt"
            :class="[
              $style.image,
              isClickListenerDefined && !shouldDisplayOverlay(sliderComponent.index)
                ? $style.clickEnabled
                : '',
            ]"
            @click="handleImageClick()"
          />

          <TenantPlusGalleryModal
            v-if="shouldDisplayOverlay(sliderComponent.index)"
            @moreImagesClick="emit('moreImagesClick')"
          />
        </div>
      </template>
    </HgSlider>
    <div v-if="$slots.default" :class="$style.controls">
      <slot :totalSlides="totalSlides" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { HgNewConstructionBadge, HgSlider, HgSpinner } from '@smg-real-estate/domus';
import { ImageAnnotation } from '@smg-real-estate/hg-rets-schema';
import { has } from 'lodash-es';
import { computed, inject, ref, useAttrs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { EventLabel, InteractionEvent, InteractionGoal } from '@/helpers/GTM/GTMParameters';
import { trackEventHarmonized } from '@/helpers/GTM/GTMTrack';
import { getListingImage } from '@/helpers/ListingData/ListingData';
import { ppaListerPrefixes } from '@/helpers/ListingStats/types';
import { VALID_ROOM_TYPES_FOR_RESTAGE } from '@/helpers/Restage/image-utils';
import { isPlatformHg as isPlatformHgFn } from '@/helpers/platformDetection';
import { GalleryContactCtaVariant } from '@/models';
import { SplitFeatureFlags } from '@/splitio/splitFeatureFlags';
import { useListingStore } from '@/store/modules/listing';
import { useRestageStore } from '@/store/modules/restage';
import { useSplitStore } from '@/store/modules/split';
import type { HgImage } from '@/store/types';

import BlurredImageBadge from './BlurredImageBadge.vue';
import ExclusiveImageBadge from './ExclusiveImageBadge.vue';
import GalleryContactCta from './GalleryContactCta.vue';
import RestageDropdown from './RestageDropdown.vue';
import RestageErrorNotification from './RestageErrorNotification.vue';
import TenantPlusGalleryModal from './TenantPlusGalleryModal.vue';

defineOptions({
  name: 'ImageGallery',
});

interface Props {
  images?: HgImage[];
  activeSlideIndex?: number;
  nextTitle?: string; // Accessibility label from the Next button.
  prevTitle?: string; // Accessibility label from the Previous button.
  shouldShowNewConstructionBadge?: boolean; // Whether it should display the new building badge.
  overlayStartIndex?: number; // The zero-based index of the first slider image to show an overlay.
  exclusiveStartIndex?: number; // The zero-based index of the first slider image to show a Plus badge.
  isArrowKeysEnabled?: boolean; // Whether the arrow keys should be enabled for navigation.
  contactCtaVariant?: GalleryContactCtaVariant;
  showContactSlide?: boolean; // Whether to append a contact slide after the last image.
}

const props = withDefaults(defineProps<Props>(), {
  images: () => [],
  activeSlideIndex: 0,
  nextTitle: 'Next',
  prevTitle: 'Previous',
  shouldShowNewConstructionBadge: false,
  overlayStartIndex: -1,
  exclusiveStartIndex: -1,
  isArrowKeysEnabled: true,
  contactCtaVariant: GalleryContactCtaVariant.COVER,
  showContactSlide: true,
});

const emit = defineEmits<{
  slideChange: [event: number];
  imageClick: [];
  moreImagesClick: [];
  contactEmailClick: [];
}>();

defineSlots<{
  default(props: { totalSlides: number }): void;
}>();

const attrs = useAttrs();
const splitStore = useSplitStore();
const listingStore = useListingStore();
const restageStore = useRestageStore();
const { t } = useI18n();

const isPlatformHg = ref<boolean>(isPlatformHgFn());
const isDisplayEventEmitted = ref<boolean>(false);
const isPreviewMode = inject<boolean>('isPreviewMode', false);
const isClickListenerDefined = computed(() => typeof attrs.onImageClick === 'function');
const isPPAListing = computed(() => {
  return ppaListerPrefixes.some((prefix) => listingStore.listing?.lister?.id?.startsWith(prefix));
});

const isRestageTreatmentOn = computed((): boolean => {
  const isRestageEnabled = splitStore.isTreatmentOn(SplitFeatureFlags.PKP_4866_RESTAGE);
  return isRestageEnabled && isPPAListing.value;
});

const isImageLoading = (index: number): boolean => {
  const originalImageUrl = props.images[index].src;
  return has(restageStore.loadingImageUrls, [originalImageUrl]);
};

const isRestageError = (index: number): boolean => {
  const originalImageUrl = props.images[index].src;
  return has(restageStore.errorImageUrls, [originalImageUrl]);
};

const originalImageAtIndex = (index: number): HgImage => {
  return props.images[index];
};

const imageAnnotationAtIndex = (index: number): ImageAnnotation | undefined => {
  if (!listingStore.listing) {
    return undefined;
  }

  const listingImageAttachment = getListingImage(listingStore.listing, index);
  if (!listingImageAttachment) {
    return undefined;
  }

  return listingImageAttachment.attachmentAnnotation?.image;
};

const effectiveImages = computed(() => {
  const images = isRestageTreatmentOn.value
    ? props.images.map((image) => restageStore.getRestagedImage(image) ?? image)
    : props.images;

  if (!props.showContactSlide) {
    return images;
  }

  return [...images, { src: '', alt: '', isContactSlide: true }];
});

const totalSlides = computed(() => effectiveImages.value.length);

const isContactSlide = (index: number): boolean =>
  props.showContactSlide && index === props.images.length;

watch(
  () => props.activeSlideIndex,
  (newActiveSlideIndex: number) => {
    if (newActiveSlideIndex === props.overlayStartIndex && !isDisplayEventEmitted.value) {
      trackEventHarmonized(InteractionEvent.FEATURE_DISPLAYED, {
        h_event_category_006: InteractionEvent.FEATURE_DISPLAYED,
        h_event_action_007: InteractionGoal.DISPLAY_BLUR_IMAGE,
        h_event_label_008: EventLabel.VIEW_BLURRY_IMAGE_PDP,
      });
      isDisplayEventEmitted.value = true;
    }
  },
);

const handleImageClick = (): void => {
  emit('imageClick');
};

/**
 * Determines if an overlay should be displayed for a given image index.
 * Overlay will only show when:
 * 1. overlayStartIndex is valid (greater or equal to 0)
 * 2. Current image index is greater than or equal to overlayStartIndex
 * This ensures no overlays are shown by default (overlayStartIndex = -1)
 */
const shouldDisplayOverlay = (index: number): boolean => {
  return props.overlayStartIndex! >= 0 && index >= props.overlayStartIndex!;
};

const shouldDisplayExclusiveBadge = (index: number): boolean => {
  return props.exclusiveStartIndex! >= 0 && index >= props.exclusiveStartIndex!;
};

const isImageValidForRestage = (index: number): boolean => {
  const annotation = imageAnnotationAtIndex(index);
  const classification = annotation?.classification;

  if (!classification) {
    return false;
  }

  return VALID_ROOM_TYPES_FOR_RESTAGE.includes(classification);
};

const shouldDisplayRestage = (index: number): boolean => {
  return (
    !isContactSlide(index) &&
    isRestageTreatmentOn.value &&
    isImageValidForRestage(index) &&
    !isPreviewMode &&
    !shouldDisplayOverlay(index) &&
    !shouldDisplayExclusiveBadge(index)
  );
};
</script>

<style lang="scss" scoped>
// Override HgSlider styles
.splide__slide img {
  height: auto;
}
</style>

<style lang="scss" module>
@import '@/assets/variables';

$controls-offset: $space-75;

section.slider {
  position: absolute;
  height: 100%;
  width: 100%;

  div[data-splide-el='controls'] {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1;
    height: fit-content;
  }

  // Splide renders the slider when it's initialized
  // but given we know that the image is ready as it's prefetched
  // we can set the visibility to visible to immediately show the first image
  // and center it
  visibility: visible;

  li[class*='HgSlider_splideSlideCenter'] {
    margin-right: 10px;
    width: calc(100% + 0px);
  }
}

.clickEnabled {
  cursor: pointer;
}

.controls {
  position: absolute;
  bottom: 0;
  box-sizing: border-box;
  width: 100%;
  padding: $controls-offset;
}

.image {
  position: relative;
  max-width: 100%;
  max-height: 100%;
}

.newConstructionProjectImage {
  width: max-content;
}

.imageContainer {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.HgNewConstructionBadge {
  width: 100%;
  border-radius: 0 !important;
}

.badge {
  margin-top: 1.25rem;
  margin-right: 1.25rem;
}

.badgeWrapper {
  z-index: 1;
  align-self: flex-end;
  height: 0;
}

.galleryWrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: fit-content;
  height: 100%;

  &.newConstructionProjectImageHg {
    height: calc(100% - 26px); // Height of Homegate-themed HgNewConstructionBadge
  }

  &.newConstructionProjectImageIs24 {
    height: calc(100% - 30px); // Height of IS24-themed HgNewConstructionBadge
  }
}

div[data-splide-el='controls'] {
  // Override the position of the glide left button control
  &[class*='HgSlider_left_'] {
    left: $space-50 !important;
  }

  // Override the position of the glide right button control
  &[class*='HgSlider_right_'] {
    right: $space-50 !important;
  }
}

.restage {
  position: absolute;
  bottom: $space-100;
  left: 50%;
  transform: translateX(-50%);

  @media (min-width: $viewport-lg) {
    top: $space-250;
    bottom: auto;
    left: auto;
    right: $space-100;
    transform: none;
  }
}

.loadingOverlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1;

  h3 {
    color: var(--theme-color-primary);
    text-shadow: 0 1px 4px rgba(0, 0, 0, 0.6);
  }
}

.toastActions {
  display: flex;
  gap: $space-100;
  margin-top: $space-50;
}
</style>
